import { Drawer, Stack, Text, Title } from '@mantine/core'
import type { CaminhoDoFluxo, MapaDoFluxo, NumerosDoFluxo } from '@/api/chamados'
import {
  ROTULO_DO_ATOR, chamados, condicaoLegivel, efeitoNoPrazo, relogioNoEstado, rotuloDoEstado, usoDoCaminho,
} from './textosDoFluxo'
import { origens, saidasDe, type FluxoVisivel, type Visao } from './visaoDoFluxo'

export type Selecao =
  | { tipo: 'estado'; estado: string }
  | { tipo: 'caminho'; caminho: CaminhoDoFluxo }

interface Props {
  selecao: Selecao | null
  mapa: MapaDoFluxo
  fluxo: FluxoVisivel
  numeros?: NumerosDoFluxo
  visao: Visao
  celular: boolean
  onFechar: () => void
}

/** O detalhe de um estado ou de um caminho: à direita no computador, de baixo no celular. */
export function DetalheDoFluxo({ selecao, mapa, fluxo, numeros, visao, celular, onFechar }: Props) {
  const titulo = !selecao ? '' : selecao.tipo === 'estado'
    ? rotuloDoEstado(selecao.estado, visao)
    : `${origens(selecao.caminho).map((e) => rotuloDoEstado(e, visao)).join(' ou ')} → ${rotuloDoEstado(selecao.caminho.para, visao)}`
  return (
    <Drawer opened={selecao !== null} onClose={onFechar}
      position={celular ? 'bottom' : 'right'} size={celular ? '70%' : 'md'}
      title={<Title order={4}>{titulo}</Title>}>
      {selecao?.tipo === 'estado' && (
        <DoEstado estado={selecao.estado} mapa={mapa} fluxo={fluxo} numeros={numeros} visao={visao} />
      )}
      {selecao?.tipo === 'caminho' && (
        <DoCaminho caminho={selecao.caminho} mapa={mapa} numeros={numeros} />
      )}
    </Drawer>
  )
}

function DoEstado({ estado, mapa, fluxo, numeros, visao }:
  { estado: string; mapa: MapaDoFluxo; fluxo: FluxoVisivel; numeros?: NumerosDoFluxo; visao: Visao }) {
  const agora = numeros?.por_estado[estado] ?? 0
  const entradas = mapa.entradas.filter((e) => e.estado === estado)
  const saidas = saidasDe(estado, fluxo.caminhos, mapa.encerrados)
  return (
    <Stack gap="md">
      <Text>{relogioNoEstado(estado, mapa)}</Text>
      {agora > 0 && (
        <Text ff="monospace">
          {numeros?.escopo === 'own' ? `Seus ${chamados(agora)} estão aqui agora.` : `${chamados(agora)} aqui agora.`}
        </Text>
      )}
      {entradas.length > 0 && (
        <Bloco titulo="O chamado nasce aqui quando">
          {entradas.map((e, i) => <Text key={i} size="sm">{condicaoLegivel(e.condicao)}</Text>)}
        </Bloco>
      )}
      <Bloco titulo="Sai daqui para">
        {saidas.length === 0
          ? <Text size="sm">Nenhuma saída nesta visão.</Text>
          : saidas.map((c, i) => (
            <Text key={i} size="sm">
              <Text span fw={600}>{rotuloDoEstado(c.para, visao)}</Text> · {condicaoLegivel(c.condicao)}
            </Text>
          ))}
      </Bloco>
    </Stack>
  )
}

function DoCaminho({ caminho, mapa, numeros }:
  { caminho: CaminhoDoFluxo; mapa: MapaDoFluxo; numeros?: NumerosDoFluxo }) {
  const efeito = efeitoNoPrazo(caminho, mapa)
  const uso = usoDoCaminho(caminho, numeros)
  return (
    <Stack gap="md">
      <Bloco titulo="Quem">
        <Text size="sm">{ROTULO_DO_ATOR[caminho.ator]}</Text>
      </Bloco>
      <Bloco titulo="Quando">
        <Text size="sm">{condicaoLegivel(caminho.condicao)}</Text>
      </Bloco>
      {efeito && (
        <Bloco titulo="No prazo">
          <Text size="sm">{efeito}</Text>
        </Bloco>
      )}
      {uso !== null && (
        <Bloco titulo="Uso">
          <Text size="sm" ff="monospace">{uso} em 90 dias</Text>
          <Text size="xs" style={{ color: 'var(--ai-text-secondary)' }}>
            Contado pelo par de estados: caminhos diferentes entre os mesmos dois estados somam juntos.
          </Text>
        </Bloco>
      )}
    </Stack>
  )
}

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="xs" fw={700} tt="uppercase" style={{ color: 'var(--ai-card-title)', letterSpacing: '0.04em' }}>
        {titulo}
      </Text>
      {children}
    </Stack>
  )
}
